import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import pl from './locales/pl/translation.json';
import en from './locales/en/translation.json';

const STORAGE_KEY = 'hussar-lang';

// Polish is the product default. We never auto-detect from navigator —
// HussarAi is a Polish-first product and English is opt-in only.
// Users can switch via the FlagToggle (Landing/Login/Register/MainApp) or
// the language dropdown in /settings; the choice is persisted to localStorage
// and (when authenticated) synced to the server via AuthContext.
let initialLng = 'pl';
try {
  const stored = typeof window !== 'undefined' && window.localStorage
    ? window.localStorage.getItem(STORAGE_KEY)
    : null;
  if (stored === 'pl' || stored === 'en') {
    initialLng = stored;
  } else if (typeof window !== 'undefined' && window.localStorage) {
    // Fresh visit — no persisted choice. Write the Polish default to
    // localStorage BEFORE the first paint so the AuthProvider's
    // "explicit client choice" guard treats this as an intentional PL
    // selection and does NOT overwrite it with a stale server-side
    // preferred_language on the first /auth/me response.
    //
    // Without this line, an authenticated user whose server-side
    // preferred_language was ever set to 'en' would land on the site in
    // English every fresh session and would need to click EN → PL
    // twice in the FlagToggle to force the switch (first click sets
    // localStorage, second click actually flips the UI). See bug report
    // 2026-02-10 "have to press en, then pol for the switch over to
    // happen".
    window.localStorage.setItem(STORAGE_KEY, 'pl');
  }
} catch {
  // localStorage unavailable (SSR/private mode) — fall back to Polish.
}

i18n
  .use(initReactI18next)
  .init({
    resources: {
      pl: { translation: pl },
      en: { translation: en },
    },
    lng: initialLng,
    fallbackLng: 'pl',
    supportedLngs: ['pl', 'en'],
    interpolation: {
      escapeValue: false,
    },
  });

// Persist explicit language switches to localStorage so the user's choice
// survives reloads even before they sign in. Also keep the top-level
// `<html lang>` attribute in sync so search engines and screen readers
// report the active locale correctly (SEO/a11y).
i18n.on('languageChanged', (lng) => {
  try {
    const normalised = lng?.startsWith('en') ? 'en' : 'pl';
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(STORAGE_KEY, normalised);
    }
    if (typeof document !== 'undefined' && document.documentElement) {
      document.documentElement.setAttribute('lang', normalised);
    }
  } catch {
    // Ignore storage errors (quota, private mode, etc.)
  }
});

// Also set the initial html lang once i18n resolves. Some pages hydrate
// before `languageChanged` fires on a warm reload.
if (typeof document !== 'undefined' && document.documentElement) {
  document.documentElement.setAttribute('lang', initialLng);
}

export default i18n;
